<template>
  <div class="batches">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="mb-0">Партии</h2>
      <button class="btn btn-success" @click="startCreate" v-if="!editing">
        Новая партия
      </button>
    </div>

    <div v-if="message" class="alert alert-success">{{ message }}</div>
    <div v-if="error" class="alert alert-danger">{{ error }}</div>

    <!-- Форма создания и правки -->
    <div class="card mb-4" v-if="editing">
      <div class="card-header bg-primary text-white">
        {{ form.id ? 'Партия ' + form.code : 'Новая партия' }}
      </div>
      <div class="card-body">
        <div class="row mb-3">
          <div class="col-md-4">
            <label class="form-label"><strong>Номер партии:</strong></label>
            <input type="text" class="form-control" v-model="form.code" placeholder="001262006">
            <div class="form-text">Как в сертификате завода. Должен быть уникальным.</div>
          </div>
          <div class="col-md-8">
            <label class="form-label">Название:</label>
            <input type="text" class="form-control" v-model="form.title" placeholder="Баллоны YSP23.9/10, март 2026">
          </div>
        </div>

        <label class="form-label"><strong>Описание:</strong></label>
        <RichText v-model="form.description_html" class="mb-3" />

        <h5 class="mt-4">Характеристики из сертификата</h5>
        <p class="text-muted small">
          Одинаковы для всей поставки, поэтому задаются один раз здесь, а не в каждой ёмкости.
          Давление в атмосферах считается из рабочего давления в МПа.
          Если у отдельного баллона нужно другое значение, его вписывают в самом паспорте —
          оно перекроет партийное.
        </p>
        <div class="row g-3 mb-3">
          <div class="col-md-4" v-for="field in specFields" :key="field.key">
            <label class="form-label">{{ field.label }}</label>
            <input
              :type="field.number ? 'number' : 'text'"
              :step="field.step"
              class="form-control"
              v-model="form[field.key]"
              :placeholder="field.placeholder || ''"
            >
          </div>
        </div>

        <div class="mb-3" v-if="form.id">
          <label class="form-label"><strong>Файл сертификата:</strong></label>
          <div v-if="form.certificate_file" class="d-flex align-items-center gap-2 mb-2">
            <a :href="`/uploads/${form.certificate_file}`" target="_blank" class="btn btn-outline-primary btn-sm">
              {{ form.certificate_name || form.certificate_file }}
            </a>
            <button class="btn btn-outline-danger btn-sm" @click="removeFile" :disabled="uploading">
              Удалить файл
            </button>
          </div>
          <input type="file" class="form-control" accept=".pdf,.jpg,.jpeg,.png" @change="uploadFile" :disabled="uploading">
          <div class="form-text">PDF, JPEG или PNG, до 20 МБ. Файл на партию один: новый заменяет прежний.</div>
        </div>
        <div class="alert alert-light border mb-3" v-else>
          Файл сертификата можно приложить сразу после создания партии.
        </div>

        <button class="btn btn-primary" @click="save" :disabled="saving || !form.code.trim()">
          {{ saving ? 'Сохраняем…' : 'Сохранить' }}
        </button>
        <button class="btn btn-secondary ms-2" @click="editing = false">Отмена</button>
      </div>
    </div>

    <!-- Список -->
    <div class="card">
      <div class="card-body p-0">
        <div v-if="loading" class="p-4 text-center text-muted">Загрузка…</div>
        <div v-else-if="batches.length === 0" class="p-4 text-center text-muted">
          Партий пока нет. Заведите первую — к ней можно будет привязать ёмкости.
        </div>
        <div v-else class="table-responsive">
        <table class="table table-hover mb-0 align-middle">
          <thead>
            <tr>
              <th>Номер</th>
              <th>Название</th>
              <th>Ёмкостей</th>
              <th>Сертификат</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="batch in batches" :key="batch.id">
              <td class="font-monospace">{{ batch.code }}</td>
              <td>{{ batch.title || '—' }}</td>
              <td>{{ batch.passports_count }}</td>
              <td>
                <a v-if="batch.certificate_file" :href="`/uploads/${batch.certificate_file}`" target="_blank">
                  {{ batch.certificate_name || 'файл' }}
                </a>
                <span v-else class="text-muted">нет</span>
              </td>
              <td class="text-end">
                <button class="btn btn-outline-secondary btn-sm" @click="startEdit(batch)">Править</button>
                <button class="btn btn-outline-danger btn-sm ms-1" @click="remove(batch)">Удалить</button>
              </td>
            </tr>
          </tbody>
        </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import RichText from '../components/RichText.vue'

// Поля сертификата — те же, что раньше заполнялись в каждом паспорте
const SPEC_FIELDS = [
  { key: 'model', label: 'Модель', placeholder: 'YSP23.9/10' },
  { key: 'manufacturer', label: 'Производитель', placeholder: 'Shandong Yong\'an Heli' },
  { key: 'gas', label: 'Газ', placeholder: 'LPG' },
  { key: 'volume', label: 'Объём, л', number: true, step: '0.1' },
  { key: 'material', label: 'Материал', placeholder: 'HP295' },
  { key: 'standard', label: 'Стандарт', placeholder: 'GB 5842' },
  { key: 'working_pressure_mpa', label: 'Рабочее давление, МПа', number: true, step: '0.01' },
  { key: 'hydro_test_pressure_mpa', label: 'Испытательное давление, МПа', number: true, step: '0.01' },
  { key: 'tightness_pressure_mpa', label: 'Давление герметичности, МПа', number: true, step: '0.01' },
  { key: 'outer_diameter_mm', label: 'Наружный диаметр, мм', number: true, step: '0.1' },
  { key: 'wall_thickness_mm', label: 'Толщина стенки, мм', number: true, step: '0.01' },
  { key: 'factory_batch', label: 'Заводской номер партии', placeholder: '001262006' },
  { key: 'manufacture_date', label: 'Дата изготовления', placeholder: '03.2026' },
  { key: 'license_number', label: 'Номер лицензии', placeholder: 'TS2237T23-2028' },
  { key: 'heat_treatment', label: 'Термообработка' }
]

function emptyForm() {
  const form = { id: null, code: '', title: '', description_html: '', certificate_file: null, certificate_name: null }
  SPEC_FIELDS.forEach((field) => { form[field.key] = '' })
  return form
}

export default {
  name: 'Batches',
  components: { RichText },
  data() {
    return {
      batches: [],
      loading: true,
      editing: false,
      saving: false,
      uploading: false,
      form: emptyForm(),
      specFields: SPEC_FIELDS,
      message: '',
      error: ''
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    authHeaders() {
      return { headers: { 'X-Admin-Auth': localStorage.getItem('adminAuth') } }
    },

    async load() {
      this.loading = true
      try {
        const response = await axios.get('/api/batches')
        this.batches = response.data.data || []
      } catch (error) {
        this.error = 'Не удалось загрузить список партий'
      } finally {
        this.loading = false
      }
    },

    startCreate() {
      this.form = emptyForm()
      this.editing = true
      this.message = ''
      this.error = ''
    },

    startEdit(batch) {
      this.form = {
        ...emptyForm(),
        id: batch.id,
        code: batch.code,
        title: batch.title || '',
        description_html: batch.description_html || '',
        certificate_file: batch.certificate_file,
        certificate_name: batch.certificate_name
      }
      SPEC_FIELDS.forEach((field) => {
        this.form[field.key] = batch[field.key] === null || batch[field.key] === undefined ? '' : batch[field.key]
      })
      this.editing = true
      this.message = ''
      this.error = ''
    },

    async save() {
      this.saving = true
      this.error = ''
      try {
        const payload = {
          code: this.form.code.trim(),
          title: this.form.title,
          description_html: this.form.description_html
        }
        SPEC_FIELDS.forEach((field) => { payload[field.key] = this.form[field.key] })
        const response = this.form.id
          ? await axios.put(`/api/batches/${this.form.id}`, payload, this.authHeaders())
          : await axios.post('/api/batches', payload, this.authHeaders())

        this.message = response.data.message
        // После создания остаёмся в форме: нужно приложить файл
        this.form.id = response.data.data.id
        await this.load()
      } catch (error) {
        this.error = (error.response && error.response.data && error.response.data.message) || 'Не удалось сохранить партию'
      } finally {
        this.saving = false
      }
    },

    async uploadFile(event) {
      const file = event.target.files[0]
      if (!file) return

      this.uploading = true
      this.error = ''
      try {
        const data = new FormData()
        data.append('file', file)
        const response = await axios.post(`/api/batches/${this.form.id}/file`, data, this.authHeaders())
        this.form.certificate_file = response.data.data.certificate_file
        this.form.certificate_name = response.data.data.certificate_name
        this.message = response.data.message
        await this.load()
      } catch (error) {
        this.error = (error.response && error.response.data && error.response.data.message) || 'Не удалось загрузить файл'
      } finally {
        this.uploading = false
        event.target.value = ''
      }
    },

    async removeFile() {
      this.uploading = true
      try {
        await axios.delete(`/api/batches/${this.form.id}/file`, this.authHeaders())
        this.form.certificate_file = null
        this.form.certificate_name = null
        this.message = 'Файл удалён'
        await this.load()
      } catch (error) {
        this.error = 'Не удалось удалить файл'
      } finally {
        this.uploading = false
      }
    },

    async remove(batch) {
      const warning = batch.passports_count > 0
        ? `Партия ${batch.code}: к ней привязано ёмкостей — ${batch.passports_count}. Они не удалятся, только отвяжутся. Удалить партию?`
        : `Удалить партию ${batch.code}?`
      if (!confirm(warning)) return

      try {
        const response = await axios.delete(`/api/batches/${batch.id}`, this.authHeaders())
        this.message = response.data.message
        if (this.form.id === batch.id) this.editing = false
        await this.load()
      } catch (error) {
        this.error = 'Не удалось удалить партию'
      }
    }
  }
}
</script>
